import { Component, inject, input, output, signal, computed } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  BackButtonComponent,
  ButtonComponent,
  IActivarTab,
  IconComponent,
  ModalComponent,
  TabsComponent,
} from '@webapp/ui';
import { IUserDetail, USER_ROLES, USER_STATUS, UserStatusValue } from '../../../interfaces/users.interface';
import { UserInfoComponent } from '../user-info/user-info.component';
import { UserPoliciesComponent } from '../user-policies/user-policies.component';
import { UserQuotesComponent } from '../user-quotes/user-quotes.component';
import { UserPaymentsComponent } from '../user-payments/user-payments.component';
import { UsersService } from '../../../services/users.service';
import { ShowForRoleDirective } from '@webapp/core/directives/showForRole';
import { AuthService } from '@webapp/auth/services/auth.service';
import { canChangeUserStatus } from '../../../common/status-permissions';

export const UserTabNames = {
  Info: 'Resumen',
  Policies: 'Pólizas',
  Quotes: 'Cotizaciones',
  PaymentMethods: 'Medios de Pago',
  // Referrals: 'Referidos',
};

@Component({
  selector: 'app-users-detail',
  imports: [
    BackButtonComponent,
    RouterLink,
    ButtonComponent,
    IconComponent,
    TabsComponent,
    UserInfoComponent,
    UserQuotesComponent,
    UserPoliciesComponent,
    UserPaymentsComponent,
    ModalComponent,
    ShowForRoleDirective
  ],
  templateUrl: './users-detail.component.html',
  styleUrl: './users-detail.component.scss',
})
export class UsersDetailComponent {
  usersService = inject(UsersService);
  private readonly auth = inject(AuthService);

  user = input<IUserDetail>();

  // Prospects have no `role` field in the backend response, so this can't be derived from `user()`.
  // The page hosting this component (users vs. prospects) knows the context and passes it explicitly.
  isProspect = input<boolean>(false);

  // Actor-vs-target gating for the status-change buttons (Activar/Desactivar/Bloquear).
  // See design D5: hidden for self, SUPERADMIN target, ADMIN-vs-ADMIN/SUPERADMIN.
  canManageStatus = computed(() =>
    canChangeUserStatus(
      this.auth.user(),
      this.user()?.id,
      this.isProspect() ? USER_ROLES.PROSPECT : this.user()?.role
    )
  );

  isActive = computed(() => this.user()?.status === USER_STATUS.ACTIVE);

  isBlocked = computed(() => this.user()?.status === USER_STATUS.BLOCKED);

  getStatusText = computed(() => {
    const status = this.user()?.status;
    if (status === USER_STATUS.ACTIVE) return 'Desactivar';
    return 'Activar';
  });

  // Optional outputs for prospect functionality
  prospectEditClick = output<void>();
  prospectDeleteClick = output<void>();

  // Optional output for user functionality
  userEditClick = output<void>();

  // ADMIN/SUPERADMIN-only client agent ("PAS") reassignment entry point.
  // See role-scope-correction: gate is ['ADMIN', 'SUPERADMIN'], not SUPERADMIN-only.
  agentReassignClick = output<void>();

  // Only CLIENT targets have a PAS to reassign — agents/brokers/admins don't.
  isClientTarget = computed(() => this.user()?.role === USER_ROLES.CLIENT);

  statusChanged = output<UserStatusValue>();

  // Modals states
  showDeleteConfirmModal = signal<boolean>(false);
  showStatusModal = signal<boolean>(false);
  showBlockModal = signal<boolean>(false);

  tabs = input<IActivarTab[]>([
    UserTabNames.Info,
    UserTabNames.Policies,
    UserTabNames.Quotes,
    UserTabNames.PaymentMethods,
    // UserTabNames.Referrals,
  ]);


  tabNames = UserTabNames;

  route = inject(ActivatedRoute);

  currentTab = signal<IActivarTab>(UserTabNames.Info);


  setCurrentTab(index: number) {
    this.currentTab.set(this.tabs()[index]);
  }

  handleUserStatus() {
    this.showStatusModal.set(true);
  }

  handleBlockUser() {
    this.showBlockModal.set(true);
  }
  
  handleProspectEditClick() {
    this.prospectEditClick.emit();
  }

  handleUserEditClick() {
    this.userEditClick.emit();
  }

  handleAgentReassignClick() {
    this.agentReassignClick.emit();
  }

  handleProspectDeleteClick() {
    this.showDeleteConfirmModal.set(true);
  }

  confirmStatusChange() {
    this.showStatusModal.set(false);
    this.statusChanged.emit(this.user()?.status === USER_STATUS.ACTIVE ? USER_STATUS.DEACTIVE : USER_STATUS.ACTIVE);
  }

  confirmBlock() {
    this.showBlockModal.set(false);
    this.statusChanged.emit(USER_STATUS.BLOCKED);
  }

  closeBlockModal() {
    this.showBlockModal.set(false);
  }

  confirmDelete() {
    this.showDeleteConfirmModal.set(false);
    this.prospectDeleteClick.emit();
  }
  closeStatusModal() {
    this.showStatusModal.set(false);
  }

  closeDeleteModal() {
    this.showDeleteConfirmModal.set(false);
  }
}
